import {
  Bookmark,
  LayoutGrid,
  MessageCircle,
  Network,
  Radio,
  Shield,
  Sparkles,
  Users,
  Boxes,
} from 'lucide-react';

/** @typedef {string} GlobalSearchTypeId */

/**
 * Canonical TYPE list — tabs and filter panel TYPE section derive from this.
 * @type {ReadonlyArray<{ id: GlobalSearchTypeId, labelKey: string, icon: import('lucide-react').LucideIcon }>}
 */
export const GLOBAL_SEARCH_TYPE_DEFINITIONS = Object.freeze([
  { id: 'agents', labelKey: 'agents', icon: Sparkles },
  { id: 'networks', labelKey: 'networks', icon: Network },
  { id: 'tickets', labelKey: 'tickets', icon: Bookmark },
  { id: 'policies', labelKey: 'policies', icon: Shield },
  { id: 'people', labelKey: 'people', icon: Users },
  { id: 'channels', labelKey: 'channels', icon: Radio },
  { id: 'conversations', labelKey: 'conversations', icon: MessageCircle },
  { id: 'workspaces', labelKey: 'workspaces', icon: Boxes },
]);

export const GLOBAL_SEARCH_TYPE_IDS = Object.freeze(
  GLOBAL_SEARCH_TYPE_DEFINITIONS.map(({ id }) => id),
);

/** @typedef {'all'|GlobalSearchTypeId} GlobalSearchCategoryId */

export const GLOBAL_SEARCH_CATEGORY_IDS = Object.freeze(['all', ...GLOBAL_SEARCH_TYPE_IDS]);

const GLOBAL_SEARCH_TYPE_DEFAULT_LABELS = Object.freeze({
  agents: 'Agents',
  networks: 'Networks',
  tickets: 'Tickets',
  policies: 'Policies',
  people: 'People',
  channels: 'Channels',
  conversations: 'Conversations',
  workspaces: 'Workspaces',
});

/**
 * Shared TYPE entities for tabs (with icons) and filter panel.
 * @param {Record<string, string>} [labels]
 * @param {typeof GLOBAL_SEARCH_TYPE_DEFINITIONS} [definitions]
 * @returns {Array<{ id: GlobalSearchTypeId, label: string, icon: import('lucide-react').LucideIcon, filterId: string }>}
 */
export function buildGlobalSearchTypeEntities(labels = {}, definitions = GLOBAL_SEARCH_TYPE_DEFINITIONS) {
  return definitions.map(({ id, labelKey, icon }) => ({
    id,
    label: labels[labelKey] ?? GLOBAL_SEARCH_TYPE_DEFAULT_LABELS[id] ?? id,
    icon,
    filterId: `type-${id}`,
  }));
}

/**
 * Category tabs: "All" + shared TYPE entities.
 * @param {Record<string, string>} [labels]
 * @param {ReturnType<typeof buildGlobalSearchTypeEntities>} [typeEntities]
 * @returns {Array<{ id: GlobalSearchCategoryId, label: string, icon: import('lucide-react').LucideIcon }>}
 */
export function buildGlobalSearchCategories(labels = {}, typeEntities = buildGlobalSearchTypeEntities(labels)) {
  return [
    { id: 'all', label: labels.all ?? 'All', icon: LayoutGrid },
    ...typeEntities.map(({ id, label, icon }) => ({ id, label, icon })),
  ];
}

/** @typedef {'type'|'owner'|'status'} GlobalSearchFilterGroupId */

/** Singular slug for TYPE chips (Figma: `type: conversation`). */
const GLOBAL_SEARCH_TYPE_CHIP_VALUES = Object.freeze({
  'type-agents': 'agent',
  'type-networks': 'network',
  'type-tickets': 'ticket',
  'type-policies': 'policy',
  'type-people': 'person',
  'type-channels': 'channel',
  'type-conversations': 'conversation',
  'type-workspaces': 'workspace',
});

/**
 * TYPE filter chips in panel — same entities/order as category tabs (without "All").
 * @param {ReturnType<typeof buildGlobalSearchTypeEntities>} typeEntities
 * @returns {Array<{ id: string, label: string }>}
 */
export function buildGlobalSearchTypeFilterOptionsFromEntities(typeEntities) {
  return typeEntities.map(({ filterId, label }) => ({ id: filterId, label }));
}

/**
 * @param {Record<string, string>} [labels]
 * @param {ReturnType<typeof buildGlobalSearchTypeEntities>} [typeEntities]
 * @returns {Array<{ id: string, label: string }>}
 */
export function buildGlobalSearchTypeFilterOptions(labels = {}, typeEntities = buildGlobalSearchTypeEntities(labels)) {
  return buildGlobalSearchTypeFilterOptionsFromEntities(typeEntities);
}

/**
 * @param {Record<string, string>} [labels]
 * @param {ReturnType<typeof buildGlobalSearchTypeEntities>} [typeEntities]
 * @returns {Array<{ id: GlobalSearchFilterGroupId, title: string, options: Array<{ id: string, label: string }> }>}
 */
export function buildGlobalSearchFilterSections(labels = {}, typeEntities = buildGlobalSearchTypeEntities(labels)) {
  return [
    {
      id: 'type',
      title: labels.filterType ?? 'Type',
      options: buildGlobalSearchTypeFilterOptionsFromEntities(typeEntities),
    },
    {
      id: 'owner',
      title: labels.filterOwner ?? 'Owner',
      options: [
        { id: 'owner-me', label: labels.ownerMe ?? 'Me' },
        { id: 'owner-edgardo', label: labels.ownerEdgardo ?? 'Edgardo Sierra' },
        { id: 'owner-sarah', label: labels.ownerSarah ?? 'Sarah Chen' },
        { id: 'owner-marcus', label: labels.ownerMarcus ?? 'Marcus Reid' },
      ],
    },
    {
      id: 'status',
      title: labels.filterStatus ?? 'Status',
      options: [
        { id: 'status-active', label: labels.statusActive ?? 'Active' },
        { id: 'status-draft', label: labels.statusDraft ?? 'Draft' },
        { id: 'status-open', label: labels.statusOpen ?? 'Open' },
        { id: 'status-in-progress', label: labels.statusInProgress ?? 'In progress' },
        { id: 'status-done', label: labels.statusDone ?? 'Done' },
        { id: 'status-archived', label: labels.statusArchived ?? 'Archived' },
      ],
    },
  ];
}

/**
 * Compact chip label (Figma: `owner: me`, `type: conversation`, `status: active`).
 * @param {string} filterId
 * @param {string} [displayLabel]
 * @returns {string}
 */
export function formatGlobalSearchFilterChipLabel(filterId, displayLabel = '') {
  const group = getGlobalSearchFilterGroupId(filterId);
  if (!group) return displayLabel;

  if (group === 'type' && GLOBAL_SEARCH_TYPE_CHIP_VALUES[filterId]) {
    return `${group}: ${GLOBAL_SEARCH_TYPE_CHIP_VALUES[filterId]}`;
  }

  if (filterId === 'status-in-progress') {
    return `${group}: in progress`;
  }

  return `${group}: ${displayLabel.toLowerCase()}`;
}

/**
 * @param {string} filterId
 * @returns {GlobalSearchFilterGroupId|null}
 */
export function getGlobalSearchFilterGroupId(filterId) {
  if (filterId.startsWith('type-')) return 'type';
  if (filterId.startsWith('owner-')) return 'owner';
  if (filterId.startsWith('status-')) return 'status';
  return null;
}

/**
 * Single-select per section. Clicking the active chip again clears that section.
 * @param {string[]} currentIds
 * @param {string} nextId
 * @returns {string[]}
 */
export function selectSingleFilterInGroup(currentIds, nextId) {
  const group = getGlobalSearchFilterGroupId(nextId);
  if (!group) return currentIds;

  const withoutGroup = currentIds.filter((id) => getGlobalSearchFilterGroupId(id) !== group);
  if (currentIds.includes(nextId)) return withoutGroup;
  return [...withoutGroup, nextId];
}

/**
 * Maps a category tab id to its TYPE filter id (when applicable).
 * @param {string} categoryId
 * @returns {string|null}
 */
export function getTypeFilterIdForCategory(categoryId) {
  if (categoryId === 'all') return null;
  if (!GLOBAL_SEARCH_TYPE_IDS.includes(categoryId)) return null;
  return `type-${categoryId}`;
}

/**
 * Derives the active category tab from applied TYPE filters.
 * @param {string[]} selectedIds
 * @returns {GlobalSearchCategoryId|string}
 */
export function getCategoryIdFromTypeFilter(selectedIds = []) {
  const typeFilter = selectedIds.find((id) => id.startsWith('type-'));
  if (!typeFilter) return 'all';

  const categoryId = typeFilter.slice('type-'.length);
  return GLOBAL_SEARCH_TYPE_IDS.includes(categoryId) ? categoryId : 'all';
}

/**
 * @param {string[]} selectedIds
 * @param {string} categoryId
 * @returns {string[]}
 */
export function applyCategoryToFilterIds(selectedIds, categoryId) {
  const withoutType = selectedIds.filter((id) => !id.startsWith('type-'));
  const typeFilterId = getTypeFilterIdForCategory(categoryId);
  if (!typeFilterId) return withoutType;
  return [...withoutType, typeFilterId];
}

/**
 * @param {string[]} selectedIds
 * @param {Record<string, string>} [labels]
 * @returns {Array<{ id: string, label: string }>}
 */
export function buildActiveFiltersFromIds(selectedIds = [], labels = {}, typeEntities = buildGlobalSearchTypeEntities(labels)) {
  const sections = buildGlobalSearchFilterSections(labels, typeEntities);
  const optionMap = new Map(
    sections.flatMap((section) =>
      section.options.map((option) => [option.id, { ...option, group: section }]),
    ),
  );

  return selectedIds
    .map((id) => {
      const match = optionMap.get(id);
      if (!match) return null;
      return {
        id,
        label: formatGlobalSearchFilterChipLabel(id, match.label),
      };
    })
    .filter(Boolean);
}

/**
 * Summary line excludes owner (Figma: `type:conversation status:active`).
 * @param {Array<{ id: string, label: string }>} activeFilters
 * @returns {string}
 */
export function buildGlobalSearchFilterSummaryText(activeFilters = []) {
  return activeFilters
    .filter((filter) => filter.id.startsWith('type-') || filter.id.startsWith('status-'))
    .map((filter) => filter.label)
    .join(' ');
}

/** Header + panel chips: violet accent in light and dark. */
export const GLOBAL_SEARCH_FILTER_CHIP_CLASS =
  'border border-violet-400/50 bg-violet-50 text-violet-900 dark:border-violet-500/50 dark:bg-violet-950/40 dark:text-violet-200';

export const GLOBAL_SEARCH_FILTER_CHIP_CLOSE_CLASS = 'text-violet-700 dark:text-violet-200';

export const GLOBAL_SEARCH_FILTER_PANEL_CLASS =
  'flex w-[298px] flex-col gap-3 rounded-lg border border-[#F2F2F2] bg-white/95 p-3 shadow-[0_4px_12px_rgba(0,0,0,0.08)] backdrop-blur-[5px] dark:border-white/10 dark:bg-[#131722]/95';

export const GLOBAL_SEARCH_FILTER_PANEL_CHIP_INACTIVE_CLASS =
  'inline-flex h-5 items-center rounded-full border border-[#5C5C5C] bg-white px-2 text-xs font-medium leading-5 text-[#2A2A2A] transition-colors hover:bg-black/[0.04] dark:border-white/15 dark:bg-[#1a2030] dark:text-gray-200 dark:hover:bg-white/[0.06]';

export const GLOBAL_SEARCH_FILTER_PANEL_CHIP_ACTIVE_CLASS =
  'inline-flex h-5 items-center rounded-full bg-violet-600 px-2 text-xs font-medium leading-5 text-white';

/** @deprecated Use GLOBAL_SEARCH_FILTER_CHIP_CLASS for violet chips. */
export const GLOBAL_SEARCH_FILTER_CHIP_STYLES = Object.freeze({
  network: {
    chipClass: GLOBAL_SEARCH_FILTER_CHIP_CLASS,
    closeClass: GLOBAL_SEARCH_FILTER_CHIP_CLOSE_CLASS,
  },
  owner: {
    chipClass: GLOBAL_SEARCH_FILTER_CHIP_CLASS,
    closeClass: GLOBAL_SEARCH_FILTER_CHIP_CLOSE_CLASS,
  },
  status: {
    chipClass: GLOBAL_SEARCH_FILTER_CHIP_CLASS,
    closeClass: GLOBAL_SEARCH_FILTER_CHIP_CLOSE_CLASS,
  },
  default: {
    chipClass: GLOBAL_SEARCH_FILTER_CHIP_CLASS,
    closeClass: GLOBAL_SEARCH_FILTER_CHIP_CLOSE_CLASS,
  },
});

export const GLOBAL_SEARCH_MODAL_PANEL_CLASS =
  'pointer-events-auto flex w-full max-w-[700px] flex-col overflow-hidden rounded-xl border border-[#BABABA] bg-white shadow-[0_24px_80px_rgba(0,0,0,0.18)] backdrop-blur-md dark:border-white/10 dark:bg-[#131722] dark:shadow-[0_24px_80px_rgba(0,0,0,0.55)]';

export const GLOBAL_SEARCH_MODAL_MAX_HEIGHT_CLASS = 'max-h-[min(600px,90vh)]';

/** Mobile (< md): fullscreen panel anchored to viewport. */
export const GLOBAL_SEARCH_MODAL_MOBILE_PANEL_CLASS =
  'max-md:w-[90vw] max-md:max-w-[90vw] max-md:h-dvh max-md:max-h-[100dvh] max-md:min-h-0 max-md:rounded-none max-md:border-0 max-md:shadow-none max-md:backdrop-blur-none';

export const GLOBAL_SEARCH_MODAL_MOBILE_CONTAINER_CLASS =
  'max-md:p-0 max-md:items-center max-md:justify-center max-md:pt-0';

export const GLOBAL_SEARCH_FILTERS_SCROLL_CLASS =
  'max-md:snap-x max-md:snap-mandatory max-md:overscroll-x-contain max-md:[-webkit-overflow-scrolling:touch]';

export const GLOBAL_SEARCH_SURFACE_CLASS = 'bg-white dark:bg-[#131722]';

export const GLOBAL_SEARCH_SEPARATOR_CLASS = 'bg-[#BABABA] dark:bg-white/10';

export const GLOBAL_SEARCH_SECTION_LABEL_CLASS =
  'px-4 py-2 text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-[#5C5C5C] dark:text-gray-500';

export const GLOBAL_SEARCH_FILTER_PANEL_SECTION_LABEL_CLASS =
  'text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-[#5C5C5C] dark:text-gray-500';

export const GLOBAL_SEARCH_KEYCAP_CLASS =
  'inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-[#5C5C5C] bg-[#F2F2F2] px-1.5 text-xs font-medium leading-none text-[#2A2A2A] dark:border-white/15 dark:bg-[#1e2433] dark:text-gray-300';

export const GLOBAL_SEARCH_ACTIVE_CATEGORY_CLASS = 'bg-violet-600 text-white';

export const GLOBAL_SEARCH_ACCENT_TEXT_CLASS =
  'text-violet-600 transition-colors hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300';

export const GLOBAL_SEARCH_INPUT_BORDER_CLASS =
  '[&_input]:border-violet-400/60 [&_input]:hover:border-violet-500/70 [&_input]:focus:border-violet-500 [&_input]:focus:ring-violet-500/25 dark:[&_input]:border-white/10 dark:[&_input]:hover:border-violet-500/50 dark:[&_input]:focus:border-violet-500/70 dark:[&_input]:focus:ring-violet-500/20';

/** Compound search field: icon + chips + text input. */
export const GLOBAL_SEARCH_COMBO_FIELD_CLASS =
  'border-violet-400/60 transition-colors hover:border-violet-500/70 focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-500/25 dark:border-white/10 dark:hover:border-violet-500/50 dark:focus-within:border-violet-500/70 dark:focus-within:ring-violet-500/20';

export const GLOBAL_SEARCH_INACTIVE_CATEGORY_CLASS =
  'text-[#2A2A2A] hover:bg-black/[0.04] dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-gray-200';
